<template>
<div class='csl-container'>

  <div class='player-container'>
    <div :class='{player: true, hidden: blocked}'>
      <video ref="videoEl"></video>
    </div>

    <div class='overlay' v-if='loading'>
      <CircularProgressBar class="reload-spinner" :value="reloadProgress" />
      <div class='reload-label subheading' v-html='reloadText'></div>
      <div class='reload-counter'>{{reloadCount}}</div>
    </div>
  </div>

  <div :class='{"validation-container":true, "will-block": willBlock}'>
    <div class="validation-label subheading">{{validationLabel}}</div>
    <ProgressBar v-if="validationProgress" class="validation-progress-bar" :value="validationProgress" />
  </div>

</div>
</template>

<script>
import { loadAsset } from '../utils/player';
import CircularProgressBar from './misc/CircularProgressBar.vue';
import ProgressBar from './misc/ProgressBar.vue';

export default {
  components: { CircularProgressBar, ProgressBar },
  name: 'DemoPageCslPlayer',
  props: {
    asset: {},
    loadDelay: {
      default: 0
    },
    expirationDate: {},
    nextPlayerExpirationDate: {},
    willBlock: { default: false }
  },
  data () {
    return {
      player: null,
      reloadProgress: 0,
      reloadText: '',
      reloadCount: 0,
      validationLabel: '',
      validationProgress: 0,
      licenseExpirationDate: 0,
      licenseExpirationDuration: 0,
      licenseExpirationUpdateDate: 0,
      licenseExpirationUpdateInterval: null,
      loadUpdateInterval: null,
      loadTimer: null,
      loadUpdateStartDate: null,
      loading: false,
      blocked: false
    };
  },
  mounted () {
    const videoElement = this.$refs.videoEl;
    const playerCfg = {
      license: this.$store.state.player_settings.license
    };

    this.player = new clpp.Player(videoElement, playerCfg);

    this.player.on(clpp.events.DRM_SESSION_UPDATE, this.onDrmSessionUpdate.bind(this));
    this.player.on(clpp.events.ERROR, e => {
      const error = e.detail;
      if (error.category !== clpp.Error.Category.DRM) {
        return;
      }
      let nextLoad = 10000;
      if (this.nextPlayerExpirationDate > 0 && isFinite(this.nextPlayerExpirationDate)) {
        nextLoad = (this.nextPlayerExpirationDate - Date.now()) - 4000;
        if (nextLoad <= 0) {
          nextLoad = 10000;
        }
      }
      if (this.licenseExpirationUpdateInterval) {
        clearInterval(this.licenseExpirationUpdateInterval);
      }
      this.validationLabel = '';
      this.validationProgress = 0;

      const errorCode = error.data.drmTodayErrorCode || 'XXXXX';
      const errorMessage = error.data.drmTodayErrorMessage || 'Reason unknown';

      this.player.release().then(() => {
        this.$emit('blocked');
        this.load(nextLoad, `<b>License renewal denied: ${errorMessage} [${errorCode}]</b><br>Reloading in`);
      });
    });

    this.load(this.loadDelay, 'Loading Content in');
  },
  beforeDestroy () {
    if (this.player) {
      this.player.off(clpp.events.DRM_SESSION_UPDATE);
      this.player.off(clpp.events.ERROR);
      this.player.destroy();
      this.player = null;
    }
    if (this.licenseExpirationUpdateInterval) {
      clearInterval(this.licenseExpirationUpdateInterval);
      this.licenseExpirationUpdateInterval = null;
    }
    if (this.loadUpdateInterval) {
      clearInterval(this.loadUpdateInterval);
      this.loadUpdateInterval = null;
    }
    if (this.loadTimer) {
      clearTimeout(this.loadTimer);
      this.loadTimer = null;
    }
  },
  methods: {
    onDrmSessionUpdate (e) {
      this.licenseExpirationUpdateDate = Date.now();
      this.licenseExpirationDate = e.expectedRenewalTime;
      this.$emit('update:expirationDate', this.licenseExpirationDate);
      this.licenseExpirationDuration = this.licenseExpirationDate - Date.now();

      if (this.licenseExpirationUpdateInterval) {
        clearInterval(this.licenseExpirationUpdateInterval);
      }
      this.licenseExpirationUpdateInterval = setInterval(() => {
        const remainMs = this.licenseExpirationDate - Date.now();
        if (remainMs <= 0) {
          this.validationLabel = '';
          this.validationProgress = 0;
          clearInterval(this.licenseExpirationUpdateInterval);
        } else {
          this.validationLabel = `Validating license in ${(remainMs / 1000).toFixed(0)}`;
          this.validationProgress = 100 * (remainMs / this.licenseExpirationDuration);
        }
      }, 1000);
    },
    load (delay, text) {
      this.loadUpdateStartDate = Date.now();
      if (this.loadUpdateInterval) {
        clearInterval(this.loadUpdateInterval);
      }

      if (delay) {
        if (text) {
          this.reloadText = text;
        }
        const updater = () => {
          const progressMs = Date.now() - this.loadUpdateStartDate;
          const remainMs = delay - progressMs;
          if (remainMs > 0) {
            this.reloadCount = Math.ceil(remainMs / 1000);
            this.reloadProgress = 100 - (100 * (remainMs / delay));
          } else {
            this.reloadText = '';
            this.reloadCount = '';
            this.reloadProgress = 100;
          }
          this.loading = true;
        };
        this.loadUpdateInterval = setInterval(() => {
          updater();
        }, 1000);
        updater();
      } else {
        this.loading = true;
        this.reloadText = '';
        this.reloadCount = '';
        this.reloadProgress = 100;
      }
      this.loadTimer = setTimeout(() => {
        loadAsset(this.player, this.asset).then(() => {
          if (this.loadUpdateInterval) {
            clearInterval(this.loadUpdateInterval);
          }
          this.blocked = false;
          this.$emit('loaded');
          this.loading = false;
        });
      }, delay || 0);
    },
    async reinit () {
      if (this.licenseExpirationUpdateInterval) {
        clearInterval(this.licenseExpirationUpdateInterval);
        this.licenseExpirationUpdateInterval = null;
      }
      if (this.loadUpdateInterval) {
        clearInterval(this.loadUpdateInterval);
        this.loadUpdateInterval = null;
      }
      if (this.loadTimer) {
        clearTimeout(this.loadTimer);
        this.loadTimer = null;
      }

      if (this.player) {
        await this.player.release();
        this.load(this.loadDelay, this.reloadText || 'Loading Content in');
      }
    }
  },
  computed: {
    reloadMode () {
      return this.reloadProgress === 100 ? 'indeterminate' : 'determinate';
    }
  }
};
</script>

<style lang="css" scoped>

.hidden {
  display: none;
}

.csl-container {
  width: 100%;
  height: 100%;
}

.validation-container {
  background-color: rgba(68,138,255,0.38);
  padding: 2px;
  height: 15%;
}

.player-container {
  background-color: black;
  position: relative;
  width: 100%;
  height: 85%;
}

.player {
  background-color: black;
  position: absolute;
  top: 0;
  left : 0;
  width: 100%;
  height: 100%;
}
.overlay {
  position: absolute;
  top: 0;
  left : 0;
  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

}
.reload-spinner {
  position: absolute;
}

.reload-label {
  position: absolute;
  text-align: center;
  padding-bottom: 220px;
  color: #DDD;
}
.reload-counter {
  position: absolute;
  text-align: center;
  color: #DDD !important;
  font-size: 3rem;
}

.validation-progress-bar {
  width: 95%;
  margin: 0 14px 5px 14px;
}

.will-block {
  transition: background-color 1s ease;
  background-color: rgb(252, 166, 6);
}

.validation-label {
  margin-left: 14px;
  padding-top: 2px;
  color: #666;
}

.subheading {
  font-size: 1.4rem;
  font-weight: 400;
  letter-spacing: .01em;
}

</style>
